/**
 * DashboardPage v1.5.0-rc4 — rediseño light/dark.
 *
 * Layout:
 *   - Header con título + fecha + refresh
 *   - 4 KPI cards (ingresos mes, cobranza pendiente, pagos hoy, clientes)
 *   - Row: donut contratos · top facturas vencidas · acciones rápidas
 *   - Row: chart facturado vs cobrado 30d · alertas operativas
 *   - Row: actividad reciente (audit log) · clientes por plan
 *
 * El componente usa solo data de gestión ByteISP — no toca monitoreo
 * (eso vive en ByteNMS). Funciona en tema claro y oscuro vía clases `dark:`.
 *
 * Cajeros siguen viendo `CajeroDashboard` (personalizado a su recaudación).
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  Calendar,
  CheckCircle2,
  ChevronRight,
  Clock,
  Loader2,
  Package,
  PauseCircle,
  Plus,
  RefreshCw,
  Receipt,
  TrendingUp,
  UserPlus,
  Users,
  Zap,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/icons/Icon";
import { apiCall } from "@/lib/api";
import { formatMXN, cn } from "@/lib/utils";

import { CajeroDashboard } from "@/routes/CajeroDashboard";
import { useAuthStore } from "@/lib/auth";
import { ContratosASuspenderModal } from "@/features/cobranza/ContratosASuspenderModal";
import { ContratosAFacturarModal } from "@/features/cobranza/ContratosAFacturarModal";
import { ContratosSuspendidosModal } from "@/features/cobranza/ContratosSuspendidosModal";

// ═════════════════════════════════════════════════════════════════════════
// Types
// ═════════════════════════════════════════════════════════════════════════
type DashboardSummary = {
  fecha_corte: string;
  clientes: { total: number; con_mora: number };
  contratos: {
    activos: number;
    suspendidos: number;
    pendientes: number;
    cancelados: number;
    error: number;
  };
  facturas: {
    hoy: { count: number; monto: string };
    mes: { count: number; monto: string };
    vencidas: { count: number; monto: string };
  };
  cobranza_pendiente: string;
  pagos: {
    hoy: { count: number; monto: string };
    mes: { count: number; monto: string };
  };
  jobs_error_24h: number;
  auto_facturacion_proxima: { candidatos: number };
  promesas_activas?: number;
  comodatos_por_recuperar?: number;
  // rc62: KPI hero re-arquitectura
  pagos_ayer?: { monto: string; delta_pct_vs_hoy: number };
  clientes_nuevos_mes?: number;
  clientes_mora_30d?: number;
  toku_excepciones?: {
    huerfanos: number;
    errores: number;
    warnings: number;
    total: number;
  };
  ultima_sincronizacion?: string | null;
};

type DashboardAlerta = {
  tipo: string;
  severidad: "critica" | "alta" | "media";
  titulo: string;
  descripcion: string;
  link: string;
  count: number;
};

type TopFacturaVencida = {
  folio: string;
  cliente_codigo: string;
  cliente_nombre: string;
  fecha_vencimiento: string;
  saldo: string;
  dias_vencida: number;
};

type Timeline30dPoint = {
  fecha: string;
  facturado: string;
  cobrado: string;
};

// rc63: Bar chart Recaudación con periodo seleccionable
type RecaudacionPeriod = "7d" | "30d" | "90d" | "1y";
type RecaudacionBar = { fecha: string; monto: string; ops: number };
type RecaudacionResponse = {
  period: RecaudacionPeriod;
  bars: RecaudacionBar[];
  total_recaudado: string;
  promedio_periodo: string;
  operaciones_total: number;
  delta_vs_periodo_anterior_pct: number;
};

// rc64: Últimos pagos + Tickets abiertos del dashboard rediseñado
type UltimoPago = {
  id: string;
  folio: string;
  cliente_id: string;
  cliente_codigo: string | null;
  cliente_nombre: string;
  factura_id: string | null;
  monto: string;
  fecha: string | null;
  payment_provider: string | null;
  created_at: string | null;
};

type TicketAbierto = {
  id: string;
  codigo: string;
  asunto: string;
  categoria: string;
  prioridad: "baja" | "normal" | "alta" | "urgente";
  estado: "abierto" | "en_proceso" | "resuelto" | "cerrado";
  cliente_codigo: string | null;
  cliente_nombre: string;
  creado_at: string | null;
  ultimo_mensaje_at: string | null;
};

type PlanesDistribution = {
  top: Array<{ plan_id: string; plan_nombre: string; count: number; pct: number }>;
  otros: { count: number; pct: number };
  total_activos: number;
};

type AuditLogItem = {
  id: string;
  actor_user_id: string | null;
  accion: string;
  entidad: string | null;
  entidad_id: string | null;
  actor_email: string | null;
  entidad_label: string | null;
  entidad_link: string | null;
  created_at: string;
};

// ═════════════════════════════════════════════════════════════════════════
// Hooks
// ═════════════════════════════════════════════════════════════════════════
function useDashboardSummary() {
  return useQuery({
    queryKey: ["dashboard", "summary"],
    queryFn: () => apiCall<DashboardSummary>("get", "/api/v1/dashboard/summary"),
    refetchInterval: 60_000,
  });
}

function useDashboardAlertas() {
  return useQuery({
    queryKey: ["dashboard", "alertas"],
    queryFn: () => apiCall<DashboardAlerta[]>("get", "/api/v1/dashboard/alertas"),
    refetchInterval: 30_000,
  });
}


function useTimeline30d() {
  return useQuery({
    queryKey: ["dashboard", "timeline-30d"],
    queryFn: () => apiCall<Timeline30dPoint[]>("get", "/api/v1/dashboard/timeline-30d"),
    refetchInterval: 300_000, // 5 min
  });
}

// rc63: hook para el bar chart de Recaudación, refetch al cambiar period
function useRecaudacion(period: RecaudacionPeriod) {
  return useQuery({
    queryKey: ["dashboard", "recaudacion", period],
    queryFn: () =>
      apiCall<RecaudacionResponse>("get", `/api/v1/dashboard/recaudacion?period=${period}`),
    refetchInterval: 120_000,
  });
}

// rc64: hooks Últimos pagos + Tickets abiertos del dashboard rediseñado
function useUltimosPagos() {
  return useQuery({
    queryKey: ["dashboard", "ultimos-pagos"],
    queryFn: () =>
      apiCall<UltimoPago[]>("get", "/api/v1/dashboard/ultimos-pagos?size=8"),
    refetchInterval: 60_000,
  });
}

function useTicketsAbiertos() {
  return useQuery({
    queryKey: ["dashboard", "tickets-abiertos"],
    queryFn: () =>
      apiCall<TicketAbierto[]>("get", "/api/v1/dashboard/tickets-abiertos?size=6"),
    refetchInterval: 60_000,
  });
}



// ═════════════════════════════════════════════════════════════════════════
// Page
// ═════════════════════════════════════════════════════════════════════════
export function DashboardPage() {
  const user = useAuthStore((s) => s.user);
  const isCajero = user?.rol?.nombre === "cajero";
  if (isCajero) {
    return <CajeroDashboard />;
  }
  return <AdminDashboard />;
}

function AdminDashboard() {
  const { data, isLoading, isFetching, refetch } = useDashboardSummary();
  const { data: alertas = [] } = useDashboardAlertas();
  // rc65: timeline sigue alimentando el sparkline del KPI #1 Recaudado hoy.
  // Las queries de topVencidas/planes/actividad se quitaron al borrar sus secciones.
  const { data: timeline } = useTimeline30d();

  // rc63: state local para period del bar chart Recaudación (default 30d)
  const [recaudacionPeriod, setRecaudacionPeriod] = useState<RecaudacionPeriod>("30d");
  const { data: recaudacion } = useRecaudacion(recaudacionPeriod);

  // rc64: Últimos pagos + Tickets abiertos
  const { data: ultimosPagos = [] } = useUltimosPagos();
  const { data: ticketsAbiertos = [] } = useTicketsAbiertos();

  // v1.5.0-rc5: modal "Servicios por suspender" — se abre desde la alerta
  // operativa correspondiente o desde la acción rápida del bloque de
  // alertas. Click fuera del modal o ESC lo cierra.
  const [suspenderModalOpen, setSuspenderModalOpen] = useState(false);
  // v1.5.0-rc10: modal "Próxima auto-facturación" — se abre desde acción
  // rápida y desde la alerta "Próxima auto-facturación" del panel.
  const [facturarModalOpen, setFacturarModalOpen] = useState(false);
  // v1.5.0-rc12: modal "Contratos suspendidos" — para reactivación y cobro.
  const [suspendidosModalOpen, setSuspendidosModalOpen] = useState(false);

  // Buscar la alerta de "contratos_a_suspender" para destacarla en el panel
  const alertaSuspender = alertas.find((a) => a.tipo === "contratos_a_suspender");
  // v1.5.0-rc12: alerta de contratos actualmente suspendidos (abre modal)
  const alertaSuspendidos = alertas.find((a) => a.tipo === "contratos_suspendidos");

  if (isLoading || !data) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 size={20} className="animate-spin" />
      </div>
    );
  }

  const cobranzaTotal = Number(data.cobranza_pendiente);
  const pagosHoyMonto = Number(data.pagos.hoy.monto);
  const pagosMesMonto = Number(data.pagos.mes.monto);
  const facturadoMes = Number(data.facturas.mes.monto);
  const vencidasMonto = Number(data.facturas.vencidas.monto);

  const contratosTotalActivos =
    data.contratos.activos + data.contratos.suspendidos + data.contratos.error;
  const totalContratos = contratosTotalActivos + data.contratos.cancelados;
  const pctActivos = contratosTotalActivos > 0
    ? (data.contratos.activos / contratosTotalActivos) * 100
    : 0;
  const pctSuspendidos = contratosTotalActivos > 0
    ? (data.contratos.suspendidos / contratosTotalActivos) * 100
    : 0;
  const pctError = contratosTotalActivos > 0
    ? (data.contratos.error / contratosTotalActivos) * 100
    : 0;

  return (
    <div className="flex flex-col gap-4">
      {/* rc62: Header con dot pulse electric + subtítulo mono + última sync alineada a la derecha.
          Match mockup A FINAL. */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <h2 className="flex items-center gap-2 text-lg font-semibold">
            <span className="inline-block h-2 w-2 rounded-full bg-electric animate-pulse" aria-hidden="true" />
            Dashboard
          </h2>
          <p className="font-mono text-[11px] text-muted-foreground">
            Estado operacional · datos en tiempo real
            {isFetching && " · actualizando…"}
          </p>
        </div>
        {data.ultima_sincronizacion && (
          <div className="text-right shrink-0">
            <div className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground">
              Última sincronización
            </div>
            <div className="font-mono text-[11px] text-foreground">
              {formatSyncTimestamp(data.ultima_sincronizacion)}
            </div>
          </div>
        )}
        <Button size="sm" variant="outline" onClick={() => refetch()} className="shrink-0">
          <Icon name="refresh" size={12} className={isFetching ? "animate-spin" : ""} />
          Refrescar
        </Button>
      </div>

      {/* rc61: Alertas críticas top banner · rc93: estilo EventCard de NMS
          (tinte rojo por severidad, caja de icono, chevron). */}
      {alertas.filter((a) => a.severidad === "critica").length > 0 && (
        <div className="space-y-2">
          {alertas
            .filter((a) => a.severidad === "critica")
            .map((a) => (
              <Link
                key={a.tipo}
                to={a.link}
                className="block rounded-lg border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/[0.06] p-2.5 no-underline transition-colors hover:border-red-300 dark:hover:border-red-500/50"
              >
                <div className="flex items-start gap-2.5">
                  <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-card">
                    <Icon name="alert" size={14} className="text-red-700 dark:text-red-400" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-medium text-red-900 dark:text-red-200">{a.titulo}</div>
                    <div className="mt-0.5 text-[10px] text-muted-foreground">{a.descripcion}</div>
                  </div>
                  <Icon name="chevron-right" size={12} className="mt-0.5 flex-shrink-0 text-red-700 dark:text-red-400" />
                </div>
              </Link>
            ))}
        </div>
      )}

      {/* ═══ rc62: KPI hero re-arquitectura match mockup ═══════════════════════
          4 cards: RECAUDADO HOY (highlight) · CLIENTES ACTIVOS · SUSPENDIDOS · EXCEPCIONES TOKU.
          KPI #1 (Recaudado hoy) destacado con border-l-electric + bg-electric/[0.06].
          Mantenemos KPICard component-style pero con datos del nuevo summary. */}
      <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-4">
        {/* #1 RECAUDADO HOY — rc89 Halo: plano con órbita C8, cifra en zafiro */}
        <Card className="halo-orbit rounded-xl p-3 shadow-sm transition-all hover:border-electric-border hover:shadow-md">
          <div className="flex items-center justify-between mb-1.5">
            <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
              Recaudado hoy
            </div>
            <Icon name="wallet" size={12} className="text-electric" />
          </div>
          <div className="font-mono text-2xl font-semibold tabular-nums tracking-tight text-electric">
            {formatMXN(pagosHoyMonto)}
          </div>
          <div className="font-mono text-[11px] text-muted-foreground mt-1 flex items-center gap-1">
            {data.pagos_ayer ? (
              <>
                <span className={data.pagos_ayer.delta_pct_vs_hoy >= 0 ? "text-success" : "text-danger"}>
                  {data.pagos_ayer.delta_pct_vs_hoy >= 0 ? "↗" : "↘"} {Math.abs(data.pagos_ayer.delta_pct_vs_hoy).toFixed(1)}%
                </span>
                <span>vs ayer ({formatMXN(Number(data.pagos_ayer.monto))})</span>
              </>
            ) : (
              <span>{data.pagos.hoy.count} operaciones hoy</span>
            )}
          </div>
          <MiniSparkline
            points={timeline?.slice(-14).map((p) => Number(p.cobrado)) ?? []}
            color="hsl(var(--electric))"
            className="mt-2"
          />
        </Card>

        {/* #2 CLIENTES ACTIVOS */}
        <Card className="halo-orbit rounded-xl p-3 shadow-sm transition-all hover:border-electric-border hover:shadow-md">
          <div className="flex items-center justify-between mb-1.5">
            <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
              Clientes activos
            </div>
            <Icon name="users" size={12} className="text-muted-foreground" />
          </div>
          <div className="font-mono text-2xl font-semibold tabular-nums tracking-tight">
            {data.contratos.activos}
          </div>
          <div className="font-mono text-[11px] text-muted-foreground mt-1">
            {(data.clientes_nuevos_mes ?? 0) > 0 && (
              <span className="text-success">↗ +{data.clientes_nuevos_mes} este mes · </span>
            )}
            <span>{data.clientes.total} clientes totales</span>
          </div>
          <div className="mt-2 flex items-center gap-3 text-[10px] font-mono">
            <span className="flex items-center gap-1 text-muted-foreground">
              <span className="w-1.5 h-1.5 rounded-full bg-success"></span>
              al día {data.clientes.total - data.clientes.con_mora}
            </span>
            <span className="flex items-center gap-1 text-muted-foreground">
              <span className="w-1.5 h-1.5 rounded-full bg-warn"></span>
              mora {data.clientes.con_mora}
            </span>
          </div>
        </Card>

        {/* #3 SUSPENDIDOS */}
        <Card className="halo-orbit rounded-xl p-3 shadow-sm transition-all hover:border-electric-border hover:shadow-md">
          <div className="flex items-center justify-between mb-1.5">
            <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
              Suspendidos
            </div>
            <Icon name="pause" size={12} className="text-warn" />
          </div>
          <div className="font-mono text-2xl font-semibold tabular-nums tracking-tight">
            {data.contratos.suspendidos}
          </div>
          <div className="font-mono text-[11px] text-muted-foreground mt-1">
            {(data.clientes_mora_30d ?? 0) > 0 ? (
              <>
                <span className="text-warn">{data.clientes_mora_30d}</span> en mora &gt;30d
              </>
            ) : (
              <span>sin clientes mora largo plazo</span>
            )}
          </div>
          <Link
            to="/cobranza"
            className="mt-2 font-mono text-[11px] text-electric hover:underline flex items-center gap-1 font-medium uppercase tracking-wider"
          >
            Ver detalle
            <Icon name="chevron-right" size={10} />
          </Link>
        </Card>

        {/* #4 EXCEPCIONES TOKU */}
        <Card className="halo-orbit rounded-xl p-3 shadow-sm transition-all hover:border-electric-border hover:shadow-md">
          <div className="flex items-center justify-between mb-1.5">
            <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
              Excepciones Toku
            </div>
            <Icon name="alert" size={12} className={(data.toku_excepciones?.total ?? 0) > 0 ? "text-warn" : "text-muted-foreground"} />
          </div>
          <div className="font-mono text-2xl font-semibold tabular-nums tracking-tight">
            {data.toku_excepciones?.total ?? 0}
          </div>
          <div className="font-mono text-[11px] text-muted-foreground mt-1">
            <span className="font-mono">{data.toku_excepciones?.huerfanos ?? 0}</span> huérfanos ·
            <span className={`ml-1 ${(data.toku_excepciones?.errores ?? 0) > 0 ? "text-danger" : ""}`}>
              {data.toku_excepciones?.errores ?? 0} err
            </span> ·
            <span className={`ml-1 ${(data.toku_excepciones?.warnings ?? 0) > 0 ? "text-warn" : ""}`}>
              {data.toku_excepciones?.warnings ?? 0} warn
            </span>
          </div>
          <Link
            to="/pagos?tab=excepciones"
            className="mt-2 font-mono text-[11px] text-electric hover:underline flex items-center gap-1 font-medium uppercase tracking-wider"
          >
            Revisar
            <Icon name="chevron-right" size={10} />
          </Link>
        </Card>
      </div>

      {/* ═══ rc63: Middle row REDISEÑADO — Bar chart Recaudación (8-col) + Alertas operativas (4-col)
              Reemplaza Donut Contratos + Top facturas + Acciones rápidas + Timeline largo.
              Match mockup A FINAL. */}
      <div className="grid grid-cols-12 gap-3">
        {/* Bar chart Recaudación con tabs 7d/30d/90d/1y */}
        <Card className="col-span-12 lg:col-span-8 p-4">
          <RecaudacionBarChart
            data={recaudacion}
            period={recaudacionPeriod}
            onPeriodChange={setRecaudacionPeriod}
          />
        </Card>

        {/* Alertas operativas — movido aquí desde el lower row */}
        <Card className="col-span-12 lg:col-span-4 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold">Alertas operativas</h3>
            {/* rc93: pill contador estilo NMS (ámbar, tabular) */}
            <span className="rounded-full border border-amber-200 bg-amber-100 px-2 py-0.5 text-[10px] tabular-nums text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/15 dark:text-amber-300">
              {alertas.length}
            </span>
          </div>
          <div className="space-y-2">
            {/* v1.5.0-rc5 — Alerta destacada: servicios por suspender mañana. */}
            {/* rc93: destacada con el estilo EventCard crítico de NMS */}
            {alertaSuspender && (
              <button
                onClick={() => setSuspenderModalOpen(true)}
                className="block w-full rounded-lg border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/[0.06] p-2.5 text-left transition-colors hover:border-red-300 dark:hover:border-red-500/50"
              >
                <div className="flex items-start gap-2.5">
                  <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-card">
                    <Icon name="alert" size={14} className="text-red-700 dark:text-red-400" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-medium text-red-900 dark:text-red-200">
                      {alertaSuspender.titulo}
                    </div>
                    <div className="mt-0.5 text-[10px] text-muted-foreground">
                      {alertaSuspender.descripcion}
                    </div>
                  </div>
                  <Icon name="chevron-right" size={12} className="mt-0.5 flex-shrink-0 text-red-700 dark:text-red-400" />
                </div>
              </button>
            )}
            {/* rc65: tones amber→warn, sky→info, neutral→info/danger según semántica.
                CTA específica por tipo de alerta (Ver / Revisar / Cobrar). */}
            {data.facturas.vencidas.count > 0 && (
              <AlertCard
                tone="warn"
                iconName="receipt"
                title={`${data.facturas.vencidas.count} factura${data.facturas.vencidas.count === 1 ? "" : "s"} vencida${data.facturas.vencidas.count === 1 ? "" : "s"}`}
                subtitle={`${formatMXN(vencidasMonto)} pendiente · acción manual`}
                cta="Ver"
                to="/facturas?estatus=vencida"
              />
            )}
            {(data.comodatos_por_recuperar ?? 0) > 0 && (
              <AlertCard
                tone="warn"
                iconName="package"
                title={`${data.comodatos_por_recuperar} comodato${data.comodatos_por_recuperar === 1 ? "" : "s"} sin recoger`}
                subtitle="contratos cancelados · técnico pendiente"
                cta="Revisar"
                to="/contratos?equipo_recuperado=false"
              />
            )}
            {data.auto_facturacion_proxima.candidatos > 0 && (
              <AlertCard
                tone="info"
                iconName="calendar"
                title="Próxima auto-facturación"
                subtitle={`${data.auto_facturacion_proxima.candidatos} contratos · click para ver detalle`}
                cta="Ver"
                onClick={() => setFacturarModalOpen(true)}
              />
            )}
            {data.jobs_error_24h > 0 && (
              <AlertCard
                tone="danger"
                iconName="alert"
                title={`${data.jobs_error_24h} job${data.jobs_error_24h === 1 ? "" : "s"} en error 24h`}
                subtitle="reintento automático activo"
                cta="Revisar"
                to="/jobs?status=error"
              />
            )}
            {(data.promesas_activas ?? 0) > 0 && (
              <AlertCard
                tone="info"
                iconName="calendar"
                title={`${data.promesas_activas} promesa${data.promesas_activas === 1 ? "" : "s"} vigente${data.promesas_activas === 1 ? "" : "s"}`}
                subtitle="dentro del periodo de gracia"
                cta="Ver"
                to="/promesas"
              />
            )}
            {alertaSuspendidos && (
              <AlertCard
                tone="warn"
                iconName="pause"
                title={alertaSuspendidos.titulo}
                subtitle="click para ver detalle y cobrar"
                cta="Cobrar"
                onClick={() => setSuspendidosModalOpen(true)}
              />
            )}
            {alertas.length === 0 &&
              data.facturas.vencidas.count === 0 &&
              (data.comodatos_por_recuperar ?? 0) === 0 &&
              data.auto_facturacion_proxima.candidatos === 0 &&
              data.jobs_error_24h === 0 &&
              (data.promesas_activas ?? 0) === 0 && (
                <div className="py-6 text-center text-xs text-muted-foreground">
                  <Icon name="check" size={18} className="mx-auto mb-2 text-success" />
                  Sin alertas activas
                </div>
              )}
          </div>
        </Card>
      </div>

      {/* ═══ rc64: Últimos pagos (8-col) + Tickets abiertos (4-col) ═══════════
          Match mockup A FINAL — row debajo del bar chart + alertas. */}
      <div className="grid grid-cols-12 gap-3">
        {/* Últimos pagos */}
        <Card className="col-span-12 lg:col-span-8 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="flex items-center gap-2 text-sm font-semibold">
              <Icon name="receipt" size={14} className="text-electric" />
              Últimos pagos
            </h3>
            <Link
              to="/pagos"
              className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground hover:text-foreground flex items-center gap-1"
            >
              Ver todos
              <Icon name="chevron-right" size={10} />
            </Link>
          </div>
          {ultimosPagos.length === 0 ? (
            <div className="py-6 text-center text-xs text-muted-foreground">
              <Icon name="wallet" size={18} className="mx-auto mb-2 text-muted-foreground/50" />
              Sin pagos recientes
            </div>
          ) : (
            <>
            {/* rc140 — móvil: lista apilada en vez de la tabla */}
            <div className="md:hidden divide-y divide-border/60">
              {ultimosPagos.map((p) => (
                <Link key={p.id} to={`/clientes/${p.cliente_id}`} className="flex items-center gap-2 py-2 no-underline">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-xs font-medium">{p.cliente_nombre}</div>
                    <div className="flex items-center gap-1.5 font-mono text-[10px] text-muted-foreground">
                      <span>{p.folio}</span>
                      <span>·</span>
                      <span>{formatTimeAgo(p.created_at)}</span>
                      <OrigenChip provider={p.payment_provider} />
                    </div>
                  </div>
                  <div className="shrink-0 font-mono text-xs tabular-nums">{formatMXN(Number(p.monto))}</div>
                </Link>
              ))}
            </div>
            <div className="overflow-x-auto max-md:hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-1.5 px-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Folio</th>
                    <th className="text-left py-1.5 px-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Cliente</th>
                    <th className="text-left py-1.5 px-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Origen</th>
                    <th className="text-right py-1.5 px-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Monto</th>
                    <th className="text-right py-1.5 px-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Hace</th>
                  </tr>
                </thead>
                <tbody>
                  {ultimosPagos.map((p) => (
                    <tr
                      key={p.id}
                      className="border-b border-border/40 border-l-2 border-l-transparent hover:border-l-electric hover:bg-electric/[0.03] transition-colors"
                    >
                      <td className="py-1.5 px-2 font-mono text-muted-foreground">{p.folio}</td>
                      <td className="py-1.5 px-2">
                        <Link to={`/clientes/${p.cliente_id}`} className="hover:underline">
                          {p.cliente_codigo && (
                            <span className="font-mono text-muted-foreground mr-1">{p.cliente_codigo}</span>
                          )}
                          <span>{p.cliente_nombre}</span>
                        </Link>
                      </td>
                      <td className="py-1.5 px-2">
                        <OrigenChip provider={p.payment_provider} />
                      </td>
                      <td className="py-1.5 px-2 text-right font-mono tabular-nums">
                        {formatMXN(Number(p.monto))}
                      </td>
                      <td className="py-1.5 px-2 text-right font-mono text-muted-foreground tabular-nums">
                        {formatTimeAgo(p.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
        </Card>

        {/* Tickets abiertos */}
        <Card className="col-span-12 lg:col-span-4 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="flex items-center gap-2 text-sm font-semibold">
              <Icon name="ticket" size={14} className="text-electric" />
              Tickets abiertos
            </h3>
            <Link
              to="/tickets"
              className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground hover:text-foreground flex items-center gap-1"
            >
              Ver todos
              <Icon name="chevron-right" size={10} />
            </Link>
          </div>
          {ticketsAbiertos.length === 0 ? (
            <div className="py-6 text-center text-xs text-muted-foreground">
              <Icon name="check" size={18} className="mx-auto mb-2 text-success" />
              Sin tickets abiertos
            </div>
          ) : (
            <div className="space-y-0">
              {ticketsAbiertos.map((t) => (
                <Link
                  key={t.id}
                  to={`/tickets/${t.id}`}
                  className="flex items-start gap-2.5 py-2 px-2 -mx-2 rounded-r border-l-2 border-l-transparent hover:border-l-electric hover:bg-electric/[0.04] transition-colors no-underline"
                >
                  <PrioridadIndicator prioridad={t.prioridad} />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium truncate">{t.asunto}</div>
                    <div className="font-mono text-[10px] text-muted-foreground truncate">
                      {t.cliente_codigo && <span>{t.cliente_codigo} · </span>}
                      <span>{t.cliente_nombre}</span> · <span>{t.codigo}</span>
                    </div>
                  </div>
                  <div className="font-mono text-[10px] text-muted-foreground whitespace-nowrap mt-0.5">
                    {formatTimeAgo(t.ultimo_mensaje_at || t.creado_at)}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </Card>
      </div>


      {/* v1.5.0-rc5 — Modal "Servicios por suspender" (dispara desde alerta) */}
      <ContratosASuspenderModal
        open={suspenderModalOpen}
        onClose={() => setSuspenderModalOpen(false)}
      />
      {/* v1.5.0-rc10 — Modal "Próxima auto-facturación" (acción rápida + alerta) */}
      <ContratosAFacturarModal
        open={facturarModalOpen}
        onClose={() => setFacturarModalOpen(false)}
      />
      {/* v1.5.0-rc12 — Modal "Contratos suspendidos" (alerta operativa) */}
      <ContratosSuspendidosModal
        open={suspendidosModalOpen}
        onClose={() => setSuspendidosModalOpen(false)}
      />
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// Subcomponents
// ═════════════════════════════════════════════════════════════════════════

function MiniSparkline({
  points,
  color,
  className = "",
}: {
  points: number[];
  color: string;
  className?: string;
}) {
  if (points.length < 2) {
    return <div className={`h-5 ${className}`} />;
  }
  const max = Math.max(...points, 1);
  const w = 100;
  const h = 22;
  const step = w / (points.length - 1);
  const path = points
    .map((p, i) => `${i * step},${h - (p / max) * (h - 4) - 2}`)
    .join(" ");
  const gradId = `g-${color.slice(1)}`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className={`w-full h-5 ${className}`}>
      <defs>
        <linearGradient id={gradId} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.2" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polyline fill={`url(#${gradId})`} stroke="none" points={`${path} ${w},${h} 0,${h}`} />
      <polyline fill="none" stroke={color} strokeWidth="1.5" points={path} />
    </svg>
  );
}

/**
 * rc63 — Bar chart de Recaudación con segmented tabs (7d/30d/90d/1y) +
 * 3 stat cards arriba (TOTAL/PROMEDIO/OPS). Reemplaza la card Donut+TopFacturas+Acciones
 * del layout viejo. Match mockup A FINAL.
 */
function RecaudacionBarChart({
  data,
  period,
  onPeriodChange,
}: {
  data: RecaudacionResponse | undefined;
  period: RecaudacionPeriod;
  onPeriodChange: (p: RecaudacionPeriod) => void;
}) {
  const labels: Record<RecaudacionPeriod, string> = {
    "7d": "últimos 7 días",
    "30d": "últimos 30 días",
    "90d": "últimos 90 días",
    "1y": "último año",
  };

  return (
    <>
      {/* Header con título + tabs segmented */}
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="flex items-center gap-2 text-sm font-semibold">
            <Icon name="activity" size={14} className="text-electric" />
            Recaudación · {labels[period]}
          </h3>
        </div>
        <div className="inline-flex rounded-md border border-border bg-card overflow-hidden">
          {(["7d", "30d", "90d", "1y"] as RecaudacionPeriod[]).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => onPeriodChange(p)}
              className={cn(
                "font-mono text-[10px] uppercase tracking-wider px-2.5 py-1 transition-colors",
                p === period
                  ? "bg-electric text-white"
                  : "text-muted-foreground hover:text-foreground hover:bg-accent/40",
              )}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      {/* Stat cards (TOTAL / PROMEDIO / OPS) */}
      {data && (
        <div className="flex items-baseline gap-6 mb-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
              Total recaudado
            </div>
            <div className="font-mono text-lg font-semibold tabular-nums tracking-tight text-electric">
              {formatMXN(Number(data.total_recaudado))}
            </div>
            <div className="font-mono text-[10px] mt-0.5">
              {data.delta_vs_periodo_anterior_pct >= 0 ? (
                <span className="text-success">
                  ↗ +{data.delta_vs_periodo_anterior_pct.toFixed(1)}% vs periodo anterior
                </span>
              ) : (
                <span className="text-danger">
                  ↘ {data.delta_vs_periodo_anterior_pct.toFixed(1)}% vs periodo anterior
                </span>
              )}
            </div>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
              Promedio diario
            </div>
            <div className="font-mono text-lg font-semibold tabular-nums tracking-tight">
              {formatMXN(Number(data.promedio_periodo))}
            </div>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
              Operaciones
            </div>
            <div className="font-mono text-lg font-semibold tabular-nums tracking-tight">
              {data.operaciones_total}
            </div>
          </div>
        </div>
      )}

      {/* Bar chart SVG */}
      {data && data.bars.length > 0 ? (
        <BarChartSVG bars={data.bars} period={period} />
      ) : (
        <div className="py-12 text-center text-xs text-muted-foreground">
          <Loader2 size={18} className="mx-auto mb-2 animate-spin" />
          cargando recaudación…
        </div>
      )}
    </>
  );
}

/**
 * rc63 — SVG puro para el bar chart, sin librería externa.
 * Bars azul electric, hover sutil con tooltip-like.
 */
function BarChartSVG({ bars, period }: { bars: RecaudacionBar[]; period: RecaudacionPeriod }) {
  const w = 700;
  const h = 140;
  const padL = 0;
  const padR = 0;
  const padB = 16;
  const maxMonto = Math.max(...bars.map((b) => Number(b.monto)), 1);

  const barWidth = Math.max(2, (w - padL - padR) / bars.length - 2);
  const gap = 2;

  return (
    <>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-32" preserveAspectRatio="none">
        {/* Bars */}
        {bars.map((b, i) => {
          const monto = Number(b.monto);
          const barH = monto > 0 ? Math.max(2, (monto / maxMonto) * (h - padB - 4)) : 0;
          const x = padL + i * (barWidth + gap);
          const y = h - padB - barH;
          return (
            <g key={b.fecha}>
              <rect
                x={x}
                y={y}
                width={barWidth}
                height={barH}
                rx={1}
                fill="hsl(var(--electric))"
                opacity={monto > 0 ? 0.95 : 0.15}
              >
                <title>
                  {formatBarTooltip(b.fecha, period)}: {formatMXN(monto)} ({b.ops} ops)
                </title>
              </rect>
            </g>
          );
        })}
        {/* baseline */}
        <line x1={0} y1={h - padB} x2={w} y2={h - padB} className="stroke-border" strokeWidth="0.5" />
      </svg>
      {/* Labels axis (3 puntos: inicio, medio, fin) */}
      <div className="flex justify-between font-mono text-[10px] text-muted-foreground tabular-nums mt-1 px-1">
        <span>{formatBarTooltip(bars[0]?.fecha, period)}</span>
        <span>{formatBarTooltip(bars[Math.floor(bars.length / 2)]?.fecha, period)}</span>
        <span>{formatBarTooltip(bars[bars.length - 1]?.fecha, period)}</span>
      </div>
    </>
  );
}

/**
 * rc64 — chip de origen para la tabla "Últimos pagos".
 * payment_provider null/manual → "Manual". toku → "Toku · SPEI" mono uppercase.
 */
function OrigenChip({ provider }: { provider: string | null }) {
  const p = (provider || "").toLowerCase();
  if (p === "toku") {
    return (
      <span className="inline-flex items-center gap-1 rounded border border-electric/40 bg-electric/[0.08] px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider text-electric">
        <Icon name="transfer" size={9} />
        Toku · SPEI
      </span>
    );
  }
  if (p === "saldo_a_favor" || p === "saldo-a-favor") {
    return (
      <span className="inline-flex items-center gap-1 rounded border border-warn/40 bg-warn/[0.08] px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider text-warn">
        <Icon name="sparkles" size={9} />
        Saldo a favor
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded border border-border bg-muted/40 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider text-muted-foreground">
      <Icon name="user" size={9} />
      Manual
    </span>
  );
}

/**
 * rc64 — indicador visual de prioridad para la lista "Tickets abiertos".
 * urgente=danger, alta=warn, normal=electric, baja=muted.
 */
function PrioridadIndicator({ prioridad }: { prioridad: TicketAbierto["prioridad"] }) {
  const m: Record<TicketAbierto["prioridad"], { color: string; iconName: import("@/components/icons/Icon").IconName }> = {
    urgente: { color: "text-danger", iconName: "alert" },
    alta:    { color: "text-warn", iconName: "alert" },
    normal:  { color: "text-electric", iconName: "ticket" },
    baja:    { color: "text-muted-foreground", iconName: "ticket" },
  };
  const v = m[prioridad];
  return (
    <Icon name={v.iconName} size={14} className={`${v.color} mt-0.5 flex-shrink-0`} />
  );
}

/**
 * rc64 — "12m" / "2h" / "3d" timeago para tablas/listas del dashboard.
 */
function formatTimeAgo(iso: string | null): string {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return "ahora";
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const d = Math.floor(hr / 24);
  if (d < 7) return `${d}d`;
  const w = Math.floor(d / 7);
  if (w < 5) return `${w}sem`;
  const mo = Math.floor(d / 30);
  return `${mo}mo`;
}

function formatBarTooltip(s: string | undefined, period: RecaudacionPeriod): string {
  if (!s) return "";
  // Period 1y → s es "YYYY-Www"; otros días → "YYYY-MM-DD"
  if (period === "1y") {
    return s.replace(/^\d{4}-/, "");  // "W22"
  }
  return formatShortDate(s);
}




// v1.5.0-rc10: variante de QuickAction que en lugar de navegar dispara un
// callback. Usado para abrir modales (ej. ContratosAFacturarModal).


/**
 * AlertCard — rc65 final del sprint (match mockup A FINAL).
 *
 * Cambio: vuelve al **icon-box** (caja pequeña 28x28 rounded-md con bg
 * tintado del color de severidad) + título + subtítulo + **CTA "REVISAR →"**
 * mono uppercase a la derecha en lugar de chevron-only. Match exacto del
 * mockup. Se eliminan los tones intermedios `sky`/`neutral` — ahora son
 * `warn`/`info`/`danger`/`neutral` con utilities específicas.
 */
function AlertCard({
  tone,
  iconName,
  title,
  subtitle,
  cta = "Revisar",
  to,
  onClick,
}: {
  tone: "warn" | "info" | "danger" | "neutral";
  iconName: import("@/components/icons/Icon").IconName;
  title: string;
  subtitle: string;
  /** Texto del CTA derecho (default "Revisar"). Mockup también usa "Ver" / "Asignar". */
  cta?: string;
  to?: string;
  onClick?: () => void;
}) {
  // rc93 — Estilo EventCard de ByteNMS: tarjeta con tinte por severidad
  // (rojo/ámbar/neutral), caja de icono bg-card, título tintado, subtítulo
  // 10px plano y chevron a la derecha. `cta` se conserva en la firma por
  // compatibilidad pero ya no se pinta (NMS usa solo chevron).
  void cta;
  const styles = {
    danger: {
      card: "border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/[0.06] hover:border-red-300 dark:hover:border-red-500/50",
      iconColor: "text-red-700 dark:text-red-400",
      titleColor: "text-red-900 dark:text-red-200",
    },
    warn: {
      card: "border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/[0.05] hover:border-amber-300 dark:hover:border-amber-500/50",
      iconColor: "text-amber-700 dark:text-amber-400",
      titleColor: "text-amber-900 dark:text-amber-200",
    },
    info: {
      card: "border-border bg-card hover:border-electric-border",
      iconColor: "text-electric",
      titleColor: "text-foreground",
    },
    neutral: {
      card: "border-border bg-card hover:border-border/80",
      iconColor: "text-muted-foreground",
      titleColor: "text-foreground",
    },
  }[tone];

  const cls = `block w-full text-left rounded-lg border ${styles.card} p-2.5 transition-colors no-underline`;
  const inner = (
    <div className="flex items-start gap-2.5">
      <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-card">
        <Icon name={iconName} size={14} className={styles.iconColor} />
      </div>
      <div className="min-w-0 flex-1">
        <div className={`text-xs font-medium ${styles.titleColor}`}>{title}</div>
        <div className="mt-0.5 text-[10px] text-muted-foreground">{subtitle}</div>
      </div>
      <Icon name="chevron-right" size={12} className={`mt-0.5 flex-shrink-0 ${styles.iconColor}`} />
    </div>
  );

  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={cls}>
        {inner}
      </button>
    );
  }
  return (
    <Link to={to ?? "#"} className={cls}>
      {inner}
    </Link>
  );
}


// ═════════════════════════════════════════════════════════════════════════
// Utils
// ═════════════════════════════════════════════════════════════════════════

function formatShortDate(iso: string | undefined): string {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("es-MX", { day: "2-digit", month: "short" });
}

/**
 * rc62: formatea timestamp ISO al estilo "DD/MM/YYYY · HH:MM:SS" mono.
 * Usado en el header "ÚLTIMA SINCRONIZACIÓN" del Dashboard.
 */
function formatSyncTimestamp(iso: string): string {
  const d = new Date(iso);
  const day = d.toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  const time = d.toLocaleTimeString("es-MX", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  return `${day} · ${time}`;
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit", hour12: false });
}

